import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CurrencyDollar, ChartLineUp, Warning, FilePdf, FloppyDisk, PencilSimple, ChartBar, TrendUp, ArrowsClockwise, Gear, Signature, Plus, Trash, PlusCircle, MinusCircle, BookOpen, LinkSimple, Info } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend, LineChart, Line, ReferenceLine, Area, ComposedChart } from "recharts";
import ClientEstimateDialog from "./ClientEstimateDialog";
import ContractorPayrollDialog from "./ContractorPayrollDialog";
import { CLAUSES } from "@/lib/clauses";
import SectionHeader from "@/components/SectionHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DevIncomePanel from "@/components/finance/DevIncomePanel";
import SpecialPaymentsPanel from "@/components/finance/SpecialPaymentsPanel";
import FinancialAnalysis from "@/pages/finanalysis/FinancialAnalysis";
import ProfitabilityPanel from "@/components/finance/ProfitabilityPanel";

// ---------------------------------------------------------------------------
// DestajoRangeTable — Vista de tabla con selector de rango de fechas que
// consolida los destajos capturados (por trabajador + turno) mostrando
// concepto, cantidad, importe y cláusulas del auditor.
// Reutiliza el mismo endpoint /finance/contractor-payroll (que ya trae el
// desglose entries[] por-turno con cláusulas), solo lo re-agrupa en filas.
// ---------------------------------------------------------------------------
function DestajoRangeTable() {
  // Iter117: default 30 días para capturar cortes de nómina mensual completos
  // (antes eran 7 días y muchas capturas de Mi Trabajo quedaban fuera del rango).
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [activeRates, setActiveRates] = useState(null);

  useEffect(() => {
    api.get("/finance/tarifas")
      .then((r) => setActiveRates(r.data?.tarifas?.destajo || {}))
      .catch(() => setActiveRates({}));
  }, []);

  const load = useCallback(async () => {
    if (!expanded) return;
    setLoading(true);
    try {
      const r = await api.get(`/finance/contractor-payroll?date_from=${dateFrom}&date_to=${dateTo}`);
      // Aplanamos: 1 fila por (trabajador × entry)
      const flat = [];
      (r.data.workers || []).forEach((w) => {
        (w.entries || []).forEach((e) => {
          flat.push({
            key: `${w.worker_id}-${e.entry_id}`,
            worker: w.name, position: w.position || "-",
            role: w.role_in_shift,
            date: e.date, shift: e.shift,
            equipment: `${e.equipment_type}${e.equipment_code ? ` · ${e.equipment_code}` : ""}`,
            area: e.area || "—",
            concepts: e.concepts || [],
            clauses: e.clauses || [],
            gross: e.gross || 0,
            deductions_total: e.deductions_total || 0,
            net: e.net || 0,
          });
        });
      });
      flat.sort((a, b) => (a.date + a.worker).localeCompare(b.date + b.worker));
      setRows(flat);
    } catch (e) { toast.error(apiError(e)); setRows([]); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, expanded]);
  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce((acc, r) => ({
    gross: acc.gross + r.gross,
    deductions_total: acc.deductions_total + r.deductions_total,
    net: acc.net + r.net,
    clauses: acc.clauses + (r.clauses.length > 0 ? 1 : 0),
  }), { gross: 0, deductions_total: 0, net: 0, clauses: 0 });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="destajo-range-table">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading font-bold text-fuchsia-700 uppercase text-xs tracking-widest flex items-center gap-1.5">
          <ChartBar size={14} weight="bold" /> Cortes de Destajo · por turno (captura Mi Trabajo → Finanzas)
        </h3>
        <button type="button" onClick={() => setExpanded((x) => !x)}
                className="ml-auto text-xs text-slate-500 hover:text-slate-800"
                data-testid="destajo-range-toggle">
          {expanded ? "Ocultar tabla ▲" : "Mostrar tabla ▼"}
        </button>
      </div>

      {expanded && (
        <>
          <div className="flex flex-wrap items-end gap-2 pb-3 border-b border-slate-100">
            <div><Label className="overline-label">Desde</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 w-40" data-testid="destajo-range-from" />
            </div>
            <div><Label className="overline-label">Hasta</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-8 w-40" data-testid="destajo-range-to" />
            </div>
            <Button size="sm" className="bg-fuchsia-700 hover:bg-fuchsia-600 text-white"
                    onClick={load} disabled={loading} data-testid="destajo-range-apply">
              {loading ? "Cargando…" : "Aplicar"}
            </Button>
            <div className="ml-auto flex gap-4 text-xs">
              <div><span className="text-slate-500">Registros:</span> <b>{rows.length}</b></div>
              <div><span className="text-slate-500">Con cláusula:</span> <b className="text-rose-600">{totals.clauses}</b></div>
              <div><span className="text-slate-500">Bruto:</span> <b className="text-sky-700">{money2(totals.gross)}</b></div>
              <div><span className="text-slate-500">Descuentos:</span> <b className="text-rose-600">-{money2(totals.deductions_total)}</b></div>
              <div><span className="text-slate-500">Neto:</span> <b className="text-emerald-700">{money2(totals.net)}</b></div>
            </div>
          </div>

          {/* Iter117: chip de tarifas activas — hace visible el vínculo captura → tarifa → pago */}
          {activeRates && Object.keys(activeRates).length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-[10px] pb-2 border-b border-slate-100"
                 data-testid="destajo-active-rates">
              <span className="overline-label text-slate-500 mr-1">Tarifas destajo activas ·</span>
              {[
                ["barrenacion_jumbero", "Barr. Jumbero", "ml"],
                ["voladuras_jumbero",   "Voladuras",     "ml"],
                ["ancla_jumbero",       "Anclaje",       "pz"],
                ["malla_jumbero",       "Malla",         "pz"],
                ["acarreo_viaje",       "Acarreo",       "viaje"],
                ["hora_scoop",          "Hr Scoop",      "h"],
              ].map(([k, lbl, u]) => (
                <span key={k} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">
                  {lbl}: <b>${activeRates[k] || 0}</b>/{u}
                </span>
              ))}
              <a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="fin-open-tarifas"]')?.click(); }}
                 className="text-blue-700 hover:underline ml-1" data-testid="destajo-goto-tarifas">
                configurar →
              </a>
            </div>
          )}

          {rows.length === 0 && !loading && (
            <p className="text-xs text-slate-400 italic py-4 text-center">
              Sin capturas de destajo entre {dateFrom} y {dateTo}.
              <br />
              <span className="text-[10px]">
                Los turnos que registran los operadores en <b>Mi Trabajo</b> aparecerán aquí calculados con las tarifas de arriba.
              </span>
            </p>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="destajo-range-body">
                <thead className="sticky top-0 bg-white z-10 border-b border-slate-200">
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="text-left px-2 py-1.5">Fecha · Turno</th>
                    <th className="text-left px-2 py-1.5">Trabajador · Rol</th>
                    <th className="text-left px-2 py-1.5">Equipo · Área</th>
                    <th className="text-left px-2 py-1.5">Destajo (concepto × cantidad × tarifa)</th>
                    <th className="text-left px-2 py-1.5">Cláusulas del auditor</th>
                    <th className="text-right px-2 py-1.5">Bruto</th>
                    <th className="text-right px-2 py-1.5">Descuento</th>
                    <th className="text-right px-2 py-1.5">Neto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.key} data-testid={`destajo-range-row-${r.key}`}
                        className={r.clauses.length > 0 ? "bg-rose-50/50" : "hover:bg-slate-50"}>
                      <td className="px-2 py-1.5 font-mono">
                        <div className="font-semibold">{r.date}</div>
                        <div className="text-[10px] text-slate-500 uppercase">{r.shift}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="font-semibold">{r.worker}</div>
                        <div className="text-[10px] text-slate-500">
                          {r.position} · <span className={r.role === "jumbero" ? "text-blue-700 font-bold" : "text-amber-700 font-bold"}>{r.role}</span>
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="text-slate-700">{r.equipment}</div>
                        <div className="text-[10px] text-slate-500">{r.area}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        {r.concepts.length === 0 ? <span className="text-slate-400">—</span> : (
                          <ul className="space-y-0.5">
                            {r.concepts.map((c, ix) => (
                              <li key={ix} className="text-[10.5px] flex flex-wrap gap-x-1.5">
                                <span className="font-medium">{c.label}:</span>
                                <span className="font-mono">{c.qty} {c.unit}</span>
                                <span className="text-slate-400">× ${c.rate}</span>
                                <span className="font-mono font-bold text-sky-700">= {money2(c.amount)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.clauses.length === 0 ? <span className="text-slate-400">—</span> : (
                          <div className="flex flex-wrap gap-1">
                            {r.clauses.map((c) => (
                              <span key={c.key} className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200"
                                    title={`${c.pct}% aplicado`}>
                                {c.key} · {c.pct}%
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-sky-700">{money2(r.gross)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-rose-600">
                        {r.deductions_total > 0 ? `-${money2(r.deductions_total)}` : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-700">{money2(r.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// Grouped input catalog for the daily capture (order matters — mimics the user's photos)
const INPUT_GROUPS = [
  {
    title: "Barrenación", accent: "amber", fields: [
      { k: "barrenacion_jum_ml", l: "ML Jumbero", u: "ml", rate: "barrenacion_jumbero" },
      { k: "barrenacion_ofi_ml", l: "ML Oficial", u: "ml", rate: "barrenacion_oficial" },
    ]
  },
  {
    title: "Voladuras", accent: "orange", fields: [
      { k: "voladuras_ofi_ml", l: "ML Oficial", u: "ml", rate: "voladuras_oficial" },
      { k: "voladuras_jum_ml", l: "ML Jumbero", u: "ml", rate: "voladuras_jumbero" },
    ]
  },
  {
    title: "Anclaje y Enmallado", accent: "emerald", fields: [
      { k: "ancla_jum_pz", l: "Ancla Boltero", u: "pz", rate: "ancla_jumbero" },
      { k: "ancla_ayd_pz", l: "Ancla Ayudante", u: "pz", rate: "ancla_ayudante" },
      { k: "malla_jum_pz", l: "Malla Boltero", u: "pz", rate: "malla_jumbero" },
      { k: "malla_ayd_pz", l: "Malla Ayudante", u: "pz", rate: "malla_ayudante" },
    ]
  },
  {
    title: "Acarreo", accent: "sky", fields: [
      { k: "viajes", l: "Viajes", u: "viaje", rate: "acarreo_viaje" },
    ]
  },
  {
    title: "Pago por Hora", accent: "violet", fields: [
      { k: "scoop_h", l: "Scoop Tram", u: "h", rate: "hora_scoop" },
      { k: "retro_h", l: "Retroexcavadora", u: "h", rate: "hora_retro" },
      { k: "tele_h", l: "Telehandler", u: "h", rate: "hora_telehandler" },
      { k: "motoniv_h", l: "Motoniveladora", u: "h", rate: "hora_motoniv" },
    ]
  },
];

const CONCEPT_KIND_COLOR = {
  avance: "#f59e0b", soporte: "#10b981", acarreo: "#0ea5e9", horas: "#a78bfa",
};

const money = (n, dec = 0) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const money2 = (n) => money(n, 2);

const emptyEntry = () => ({
  date: new Date().toISOString().slice(0, 10),
  area: "", project_id: defaultProject(), shift: "dia",
  destajo: {}, penalizaciones: {}, notes: "",
});

// -------- Header KPI card --------
function KpiCard({ label, value, sub, tone = "slate", testid }) {
  const styles = {
    slate:   { bg: "bg-white border-slate-200",       val: "text-slate-900",  label: "text-slate-500" },
    emerald: { bg: "bg-emerald-50 border-emerald-200", val: "text-emerald-800", label: "text-emerald-700" },
    amber:   { bg: "bg-amber-50 border-amber-200",     val: "text-amber-800",   label: "text-amber-700" },
    rose:    { bg: "bg-rose-50 border-rose-200",       val: "text-rose-800",    label: "text-rose-700" },
    sky:     { bg: "bg-sky-50 border-sky-200",         val: "text-sky-800",     label: "text-sky-700" },
  }[tone];
  return (
    <div className={`${styles.bg} border rounded-lg p-4 shadow-sm`} data-testid={testid}>
      <p className={`text-[10px] uppercase tracking-widest font-bold ${styles.label} mb-1`}>{label}</p>
      <p className={`font-heading font-black text-2xl ${styles.val} leading-tight`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

// -------- Tarifas dialog --------
function TarifasDialog({ tarifas, onClose, onSaved }) {
  const [dest, setDest] = useState({ ...tarifas.destajo });
  const [cli, setCli] = useState({ ...tarifas.client });
  const [pen, setPen] = useState({ ...tarifas.penalties });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/finance/tarifas", { destajo: dest, client: cli, penalties: pen });
      toast.success("Tarifas actualizadas");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const num = (v) => (v === "" ? 0 : parseFloat(v) || 0);
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-amber-600">Tarifas del contrato</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Destajo = lo que TÚ pagas a personal · Cliente = lo que la mina te paga.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div>
            <h4 className="font-heading font-bold text-emerald-700 mb-2 uppercase text-xs tracking-widest">Destajo (personal)</h4>
            <div className="space-y-1">
              {Object.entries(dest).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <Label className="text-[11px] text-slate-700 flex-1 truncate">{k}</Label>
                  <Input type="number" step="0.01" value={v} onChange={(e) => setDest({ ...dest, [k]: num(e.target.value) })}
                    className="h-8 w-28 bg-white border-slate-200 text-slate-900" data-testid={`fin-tarifa-destajo-${k}`} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-heading font-bold text-sky-700 mb-2 uppercase text-xs tracking-widest">Cliente (mina te paga)</h4>
            <div className="space-y-1">
              {Object.entries(cli).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <Label className="text-[11px] text-slate-700 flex-1 truncate">{k}</Label>
                  <Input type="number" step="0.01" value={v} onChange={(e) => setCli({ ...cli, [k]: num(e.target.value) })}
                    className="h-8 w-28 bg-white border-slate-200 text-slate-900" data-testid={`fin-tarifa-cliente-${k}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="font-heading font-bold text-rose-600 mb-2 uppercase text-xs tracking-widest">Penalizaciones (% descuento)</h4>
          <div className="grid md:grid-cols-2 gap-1">
            {Object.entries(pen).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <Label className="text-[11px] text-slate-700 flex-1 truncate">{k}</Label>
                <Input type="number" step="0.01" value={v} onChange={(e) => setPen({ ...pen, [k]: num(e.target.value) })}
                  className="h-8 w-24 bg-white border-slate-200 text-slate-900" data-testid={`fin-tarifa-penalty-${k}`} />
                <span className="text-slate-500 text-xs">%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-700 hover:bg-slate-50">Cancelar</Button>
          <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="fin-tarifas-save">
            <FloppyDisk size={14} weight="bold" className="mr-1" /> {busy ? "Guardando…" : "Guardar tarifas"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Budget dialog --------
function BudgetDialog({ month, current, onClose, onSaved }) {
  const [pickedMonth, setPickedMonth] = useState(month);
  const [f, setF] = useState({
    month,
    ingreso_target: current?.ingreso_target || 0,
    costo_target: current?.costo_target || 0,
    margen_target_pct: current?.margen_target_pct || 20,
    meta_avance_ml: current?.meta_avance_ml || 0,
  });
  const [busy, setBusy] = useState(false);
  const [loadingMonth, setLoadingMonth] = useState(false);

  // Al cambiar el mes destino, cargamos su budget (si existe) para pre-poblar
  // el formulario y evitar sobrescribir ciegamente el de otro mes.
  useEffect(() => {
    if (!pickedMonth || pickedMonth === month) {
      setF((prev) => ({ ...prev, month: pickedMonth }));
      return;
    }
    setLoadingMonth(true);
    api.get(`/finance/budget?month=${pickedMonth}`)
      .then((r) => {
        const b = r.data?.budget || {};
        setF({
          month: pickedMonth,
          ingreso_target: b.ingreso_target || 0,
          costo_target: b.costo_target || 0,
          margen_target_pct: b.margen_target_pct || 20,
          meta_avance_ml: b.meta_avance_ml || 0,
        });
      })
      .catch(() => setF((prev) => ({ ...prev, month: pickedMonth })))
      .finally(() => setLoadingMonth(false));
  }, [pickedMonth, month]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/finance/budget", { ...f, month: pickedMonth });
      toast.success(`Budget guardado para ${pickedMonth}`);
      onSaved();
    }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-amber-600" data-testid="fin-budget-title">Budget mensual · {pickedMonth}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-700">Mes de asignación</Label>
            <Input
              type="month"
              value={pickedMonth}
              onChange={(e) => setPickedMonth(e.target.value || month)}
              className="bg-white border-slate-200 text-slate-900"
              data-testid="fin-budget-month"
            />
            {loadingMonth && <p className="text-[10px] text-slate-500 mt-1">Cargando budget existente…</p>}
            {!loadingMonth && pickedMonth !== month && (
              <p className="text-[10px] text-amber-700 mt-1">Editarás el budget del mes seleccionado, no del mes actual.</p>
            )}
          </div>
          <div><Label className="text-xs text-slate-700">Ingreso objetivo (MXN)</Label>
            <Input type="number" step="0.01" value={f.ingreso_target} onChange={(e) => setF({ ...f, ingreso_target: parseFloat(e.target.value) || 0 })}
              className="bg-white border-slate-200 text-slate-900" data-testid="fin-budget-ingreso" /></div>
          <div><Label className="text-xs text-slate-700">Costo objetivo (MXN)</Label>
            <Input type="number" step="0.01" value={f.costo_target} onChange={(e) => setF({ ...f, costo_target: parseFloat(e.target.value) || 0 })}
              className="bg-white border-slate-200 text-slate-900" data-testid="fin-budget-costo" /></div>
          <div><Label className="text-xs text-slate-700">% Margen objetivo</Label>
            <Input type="number" step="0.1" value={f.margen_target_pct} onChange={(e) => setF({ ...f, margen_target_pct: parseFloat(e.target.value) || 0 })}
              className="bg-white border-slate-200 text-slate-900" data-testid="fin-budget-margen" /></div>
          <div><Label className="text-xs text-slate-700">Meta avance (ml)</Label>
            <Input type="number" step="0.01" value={f.meta_avance_ml} onChange={(e) => setF({ ...f, meta_avance_ml: parseFloat(e.target.value) || 0 })}
              className="bg-white border-slate-200 text-slate-900" data-testid="fin-budget-meta-ml" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-700 hover:bg-slate-50">Cancelar</Button>
          <Button onClick={save} disabled={busy || loadingMonth} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="fin-budget-save">
            {busy ? "Guardando…" : "Guardar budget"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Annual Budget dialog (vista 12 meses editable + real) --------
const MONTH_LABELS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function AnnualBudgetDialog({ initialYear, onClose, onSaved }) {
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);     // budget rows editables
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const fmt = (n) => (n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });
  const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;
  const varClass = (delta, positiveGood = true) => {
    if (!delta) return "text-slate-400";
    const good = positiveGood ? delta > 0 : delta < 0;
    return good ? "text-emerald-600" : "text-red-600";
  };
  const varSymbol = (a, b) => {
    const d = (a || 0) - (b || 0);
    if (!d) return "0";
    return (d > 0 ? "+" : "") + d.toLocaleString("es-MX", { maximumFractionDigits: 0 });
  };

  const load = useCallback(async (y) => {
    setBusy(true);
    try {
      const { data: r } = await api.get(`/finance/budget/annual?year=${y}`);
      setData(r);
      setRows(r.months.map((m) => ({
        month: m.month,
        ingreso_target: m.budget.ingreso_target,
        costo_target: m.budget.costo_target,
        margen_target_pct: m.budget.margen_target_pct,
        meta_avance_ml: m.budget.meta_avance_ml,
        _dirty: false,
      })));
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  const updRow = (i, key, val) => {
    setRows((prev) => prev.map((r, idx) => idx === i
      ? { ...r, [key]: parseFloat(val) || 0, _dirty: true } : r));
  };
  const copyPrev = (i) => {
    if (i === 0) return;
    setRows((prev) => prev.map((r, idx) => idx === i ? {
      ...r,
      ingreso_target: prev[i - 1].ingreso_target,
      costo_target: prev[i - 1].costo_target,
      margen_target_pct: prev[i - 1].margen_target_pct,
      meta_avance_ml: prev[i - 1].meta_avance_ml,
      _dirty: true,
    } : r));
  };
  const dirty = useMemo(() => rows.filter((r) => r._dirty), [rows]);

  const saveAll = async () => {
    if (!dirty.length) { toast.info("No hay cambios que guardar"); return; }
    setSaving(true);
    try {
      const payload = dirty.map(({ _dirty, ...rest }) => rest);
      const { data: r } = await api.put("/finance/budget/bulk", payload);
      toast.success(`${r.saved} mes(es) actualizados`);
      onSaved?.();
      load(year);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="fin-annual-budget-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-amber-600 flex items-center gap-3">
            <ChartBar size={18} weight="fill" />
            Vista anual · Budget vs Real · {year}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3 border-b border-slate-200 pb-2">
          <Label className="text-xs text-slate-700">Año</Label>
          <Input type="number" value={year} min={2000} max={2100}
                 onChange={(e) => setYear(parseInt(e.target.value, 10) || initialYear)}
                 className="w-28" data-testid="fin-annual-year" />
          {dirty.length > 0 && (
            <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
              {dirty.length} mes(es) con cambios sin guardar
            </span>
          )}
          <div className="flex-1" />
          {data?.totals && (
            <div className="text-right text-[11px] leading-tight">
              <p className="text-slate-500">Total budget año · Ingreso vs Real</p>
              <p className="font-bold">
                <span className="text-slate-900">${fmt(data.totals.budget.ingreso_target)}</span>
                <span className="text-slate-400 mx-1">·</span>
                <span className="text-emerald-700">${fmt(data.totals.actual.ingreso_neto)}</span>
              </p>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-auto">
          {busy ? (
            <p className="text-center text-slate-500 py-8">Cargando…</p>
          ) : (
            <table className="w-full text-xs" data-testid="fin-annual-budget-table">
              <thead className="sticky top-0 bg-slate-900 text-white">
                <tr>
                  <th className="px-2 py-2 text-left w-16">Mes</th>
                  <th className="px-2 py-2 text-right" title="Ingreso objetivo (MXN)">Ing. Budget</th>
                  <th className="px-2 py-2 text-right" title="Ingreso neto realizado (desde daily entries)">Ing. Real</th>
                  <th className="px-2 py-2 text-right" title="Costo objetivo (MXN)">Costo Budget</th>
                  <th className="px-2 py-2 text-right" title="Costo destajo real">Costo Real</th>
                  <th className="px-2 py-2 text-right" title="% margen objetivo">%Mrg B.</th>
                  <th className="px-2 py-2 text-right" title="% margen real">%Mrg R.</th>
                  <th className="px-2 py-2 text-right" title="Meta avance en metros lineales">Meta ml</th>
                  <th className="px-2 py-2 text-right" title="Metros lineales realmente ejecutados">ml Real</th>
                  <th className="px-2 py-2 text-right" title="Variación de ingreso (real - budget)">Δ Ingreso</th>
                  <th className="px-2 py-2 text-center w-16">Copiar</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const m = data?.months?.[i];
                  const act = m?.actual || {};
                  const dIng = (act.ingreso_neto || 0) - (r.ingreso_target || 0);
                  const dCost = (act.costo_destajo || 0) - (r.costo_target || 0);
                  const isCurrent = m?.month === new Date().toISOString().slice(0, 7);
                  return (
                    <tr key={r.month}
                        className={`border-b border-slate-100 hover:bg-amber-50/40 ${r._dirty ? "bg-amber-50/50" : ""} ${isCurrent ? "ring-1 ring-inset ring-blue-300" : ""}`}
                        data-testid={`fin-annual-row-${r.month}`}>
                      <td className="px-2 py-1 font-bold text-slate-700">
                        {MONTH_LABELS_ES[i]}
                        {isCurrent && <span className="ml-1 text-[9px] font-bold text-blue-700">•</span>}
                      </td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" step="1000" value={r.ingreso_target}
                               onChange={(e) => updRow(i, "ingreso_target", e.target.value)}
                               className="h-7 text-xs text-right" data-testid={`fin-annual-ing-${r.month}`} />
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-700">${fmt(act.ingreso_neto)}</td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" step="1000" value={r.costo_target}
                               onChange={(e) => updRow(i, "costo_target", e.target.value)}
                               className="h-7 text-xs text-right" data-testid={`fin-annual-costo-${r.month}`} />
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-red-600">${fmt(act.costo_destajo)}</td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" step="0.5" value={r.margen_target_pct}
                               onChange={(e) => updRow(i, "margen_target_pct", e.target.value)}
                               className="h-7 text-xs text-right w-16 ml-auto" data-testid={`fin-annual-mrg-${r.month}`} />
                      </td>
                      <td className={`px-2 py-1 text-right tabular-nums font-semibold ${act.margen_pct >= r.margen_target_pct ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(act.margen_pct)}</td>
                      <td className="px-1 py-1 text-right">
                        <Input type="number" step="0.5" value={r.meta_avance_ml}
                               onChange={(e) => updRow(i, "meta_avance_ml", e.target.value)}
                               className="h-7 text-xs text-right w-20 ml-auto" data-testid={`fin-annual-ml-${r.month}`} />
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(act.avance_ml)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-bold ${varClass(dIng)}`}>
                        {r.ingreso_target ? `${dIng >= 0 ? "+" : ""}${fmt(dIng)}` : "-"}
                      </td>
                      <td className="px-1 py-1 text-center">
                        <button
                          onClick={() => copyPrev(i)}
                          disabled={i === 0}
                          className="text-[10px] font-bold text-blue-700 hover:text-blue-900 disabled:text-slate-300 disabled:cursor-not-allowed"
                          title="Copiar del mes anterior"
                          data-testid={`fin-annual-copy-${r.month}`}
                        >
                          ↑ prev
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {data?.totals && (
                <tfoot className="sticky bottom-0 bg-slate-900 text-white">
                  <tr>
                    <td className="px-2 py-2 font-black">TOTAL</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold">${fmt(data.totals.budget.ingreso_target)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold text-emerald-300">${fmt(data.totals.actual.ingreso_neto)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold">${fmt(data.totals.budget.costo_target)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold text-red-300">${fmt(data.totals.actual.costo_destajo)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold">{fmtPct(data.totals.budget.margen_pct)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold text-amber-300">{fmtPct(data.totals.actual.margen_pct)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold">{fmt(data.totals.budget.meta_avance_ml)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold" />
                    <td className={`px-2 py-2 text-right tabular-nums font-black ${varClass(data.totals.actual.ingreso_neto - data.totals.budget.ingreso_target)}`}>
                      {varSymbol(data.totals.actual.ingreso_neto, data.totals.budget.ingreso_target)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
        <div className="flex justify-between items-center gap-2 pt-3 border-t border-slate-200">
          <p className="text-[10px] text-slate-500 italic">
            Los valores de &quot;Real&quot; provienen automáticamente de los daily entries del módulo Operaciones (ingreso neto, costo destajo, metros lineales).
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-700 hover:bg-slate-50">Cerrar</Button>
            <Button onClick={saveAll} disabled={saving || busy || !dirty.length}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold gap-1"
                    data-testid="fin-annual-save-all">
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Guardando…" : `Guardar ${dirty.length || ""}`.trim()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Main page --------

// -------- Ledger entry dialog (Ingreso / Gasto manual) --------
function LedgerDialog({ mode, entry, cats, onClose, onSaved }) {
  const [f, setF] = useState(entry);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const availableCats = useMemo(
    () => (f.kind === "ingreso" ? (cats.ingreso || []) : (cats.gasto || [])),
    [f.kind, cats.ingreso, cats.gasto],
  );
  useEffect(() => {
    // if user toggled kind, reset category to first of new kind if current one doesn't apply
    if (!availableCats.includes(f.category)) upd("category", availableCats[0] || "otro");
  }, [f.kind, availableCats, f.category]);

  const isIng = f.kind === "ingreso";
  // Iter118: modo Factura sólo aplica a gastos. Se activa si el usuario ya
  // capturó partidas o marcó la casilla; permite alternar entre gasto simple
  // (monto directo) y factura con partidas + IVA.
  const [invoiceMode, setInvoiceMode] = useState(() => !isIng && (entry.items || []).length > 0);
  const items = f.items || [];
  const setItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setF((s) => ({ ...s, items: next }));
  };
  const addItem = () => setF((s) => ({ ...s, items: [...(s.items || []), { description: "", qty: 0, unit_price: 0, total: 0 }] }));
  const removeItem = (idx) => setF((s) => ({ ...s, items: (s.items || []).filter((_, i) => i !== idx) }));
  const subtotal = items.reduce((acc, it) => {
    const q = parseFloat(it.qty) || 0, up = parseFloat(it.unit_price) || 0, t = parseFloat(it.total) || 0;
    return acc + (q > 0 && up > 0 ? q * up : t);
  }, 0);
  const ivaRate = parseFloat(f.iva_rate) || 0;
  const totalIva = subtotal * (1 + ivaRate / 100);
  const fxRate = parseFloat(f.fx_rate) || 1;

  const save = async () => {
    if (!f.concept.trim()) { toast.error("Escribe una descripción del concepto"); return; }
    let amount = parseFloat(f.amount) || 0;
    const qty = parseFloat(f.qty) || 0;
    const up = parseFloat(f.unit_price) || 0;
    // Iter118: si hay partidas, el monto contable = total con IVA × tipo de cambio
    if (invoiceMode && items.length > 0) {
      amount = Math.round(totalIva * fxRate * 100) / 100;
    } else if (qty > 0 && up > 0) {
      amount = Math.round(qty * up * fxRate * 100) / 100;
    } else if (amount > 0 && fxRate !== 1) {
      amount = Math.round(amount * fxRate * 100) / 100;
    }
    if (amount <= 0) { toast.error("Ingresa monto o cantidad × precio unitario o partidas"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f, amount, qty, unit_price: up,
        fx_rate: fxRate,
        subtotal: Math.round(subtotal * 100) / 100,
        iva_rate: ivaRate,
        total_with_iva: Math.round(totalIva * 100) / 100,
        items: invoiceMode ? items.map((it) => ({
          description: it.description || "",
          qty: parseFloat(it.qty) || 0,
          unit_price: parseFloat(it.unit_price) || 0,
          total: (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0) > 0
            ? Math.round((parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0) * 100) / 100
            : parseFloat(it.total) || 0,
        })) : [],
      };
      if (!assertProject(f.project_id)) return;
      if (mode === "edit") await api.put(`/finance/ledger/${f.id}`, payload);
      else await api.post("/finance/ledger", payload);
      toast.success(`${isIng ? "Ingreso" : "Gasto"} guardado`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const tone = isIng ? "emerald" : "rose";
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className={`font-heading flex items-center gap-2 ${isIng ? "text-emerald-700" : "text-rose-600"}`}>
            {isIng ? <PlusCircle size={20} weight="fill" /> : <MinusCircle size={20} weight="fill" />}
            {mode === "edit" ? "Editar" : "Nuevo"} {isIng ? "Ingreso" : "Gasto"}
          </DialogTitle>
        </DialogHeader>
        <div className={`bg-${tone}-900/40 border border-${tone}-800/50 rounded p-2 flex gap-2 text-xs`}>
          <button type="button" onClick={() => { upd("kind", "ingreso"); setInvoiceMode(false); }}
            className={`flex-1 py-1 rounded font-bold ${isIng ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid="ledger-tab-ingreso">
            + INGRESO
          </button>
          <button type="button" onClick={() => upd("kind", "gasto")}
            className={`flex-1 py-1 rounded font-bold ${!isIng ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid="ledger-tab-gasto">
            – GASTO
          </button>
        </div>
        <div className="space-y-3">
          {/* Iter118: fila común Fecha + Categoría + Moneda + Tipo de cambio */}
          <div className="grid grid-cols-4 gap-2">
            <ProjectField compact value={f.project_id} onChange={(v) => upd("project_id", v)} testid="ledger-project" />
            <div><Label className="text-[10px] text-slate-500 uppercase">Fecha</Label>
              <Input type="date" value={f.date} onChange={(e) => upd("date", e.target.value)}
                className="bg-white border-slate-200 text-slate-900" data-testid="ledger-date" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Categoría</Label>
              <Select value={f.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-900" data-testid="ledger-cat"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900">
                  {availableCats.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Moneda</Label>
              <Select value={f.currency || "MXN"} onValueChange={async (v) => {
                upd("currency", v);
                if (v === "MXN") { upd("fx_rate", 1); return; }
                // Iter120: auto-completar tipo de cambio desde API pública
                try {
                  const { data } = await api.get(`/finance/fx?currency=${v}&base=MXN`);
                  if (data?.rate > 0) {
                    upd("fx_rate", data.rate);
                    toast.success(`Tipo de cambio ${v}→MXN: ${data.rate} (${data.source})`);
                  }
                } catch { /* fallback: usuario captura manual */ }
              }}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-900" data-testid="ledger-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="MXN">MXN</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="text-[10px] text-slate-500 uppercase" title="Multiplicador a MXN. 1 = misma moneda">Tipo de cambio</Label>
              <Input type="number" step="0.0001" min="0" value={f.fx_rate ?? 1}
                onChange={(e) => upd("fx_rate", e.target.value)}
                disabled={(f.currency || "MXN") === "MXN"}
                className="bg-white border-slate-200 text-slate-900 text-right font-mono" data-testid="ledger-fx-rate" /></div>
          </div>

          <div><Label className="text-[10px] text-slate-500 uppercase">Concepto / descripción *</Label>
            <Input value={f.concept} onChange={(e) => upd("concept", e.target.value)}
              placeholder={isIng ? "ej. 1200 ml barrenación acumulada" : "ej. Diesel julio 3200 L"}
              className="bg-white border-slate-200 text-slate-900" data-testid="ledger-concept" /></div>

          {/* Iter118: sección FACTURA — sólo egresos */}
          {!isIng && (
            <div className="border border-rose-200 bg-rose-50/50 rounded-md p-3 space-y-2" data-testid="ledger-invoice-section">
              <div className="flex items-center justify-between">
                <p className="overline-label text-rose-700">🧾 Comprobante fiscal</p>
                <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={invoiceMode}
                    onChange={(e) => setInvoiceMode(e.target.checked)}
                    data-testid="ledger-invoice-toggle" />
                  Este gasto tiene factura con partidas
                </label>
              </div>
              {invoiceMode && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px] text-slate-500 uppercase">Nº Factura</Label>
                      <Input value={f.invoice_number || ""} onChange={(e) => upd("invoice_number", e.target.value)}
                        placeholder="F-2025-00123" className="bg-white border-slate-200" data-testid="ledger-invoice-num" /></div>
                    <div><Label className="text-[10px] text-slate-500 uppercase">Fecha factura</Label>
                      <Input type="date" value={f.invoice_date || ""} onChange={(e) => upd("invoice_date", e.target.value)}
                        className="bg-white border-slate-200" data-testid="ledger-invoice-date" /></div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <p className="overline-label text-slate-700">Partidas</p>
                      <button type="button" onClick={addItem}
                        className="text-[11px] font-bold text-rose-700 hover:text-rose-800"
                        data-testid="ledger-add-item">+ Agregar partida</button>
                    </div>
                    {items.length === 0 && (
                      <p className="text-[10px] italic text-slate-400 text-center py-1">Sin partidas — pulsa «+ Agregar partida».</p>
                    )}
                    {items.map((it, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_60px_80px_80px_28px] gap-1 items-end"
                           data-testid={`ledger-item-${idx}`}>
                        <div>
                          <Label className="text-[9px] uppercase text-slate-500">Descripción</Label>
                          <Input value={it.description || ""} onChange={(e) => setItem(idx, { description: e.target.value })}
                            placeholder={`Partida ${idx + 1}`} className="h-8 text-xs" data-testid={`ledger-item-desc-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[9px] uppercase text-slate-500">Cant.</Label>
                          <Input type="number" step="0.01" value={it.qty || ""} onChange={(e) => setItem(idx, { qty: e.target.value })}
                            className="h-8 text-xs text-right font-mono" data-testid={`ledger-item-qty-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[9px] uppercase text-slate-500">P.Unit.</Label>
                          <Input type="number" step="0.01" value={it.unit_price || ""} onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                            className="h-8 text-xs text-right font-mono" data-testid={`ledger-item-up-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[9px] uppercase text-slate-500">Total</Label>
                          <Input type="number" step="0.01"
                            value={((parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0)) > 0
                              ? ((parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0)).toFixed(2)
                              : (it.total || "")}
                            onChange={(e) => setItem(idx, { total: e.target.value })}
                            disabled={(parseFloat(it.qty) || 0) > 0 && (parseFloat(it.unit_price) || 0) > 0}
                            className="h-8 text-xs text-right font-mono font-bold" data-testid={`ledger-item-total-${idx}`} />
                        </div>
                        <button type="button" onClick={() => removeItem(idx)}
                          className="h-8 w-7 text-slate-400 hover:text-red-600 text-lg" data-testid={`ledger-item-remove-${idx}`}>×</button>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2 items-end">
                    <div className="text-right">
                      <p className="text-[10px] uppercase text-slate-500">Subtotal</p>
                      <p className="font-mono font-bold text-sm text-slate-900" data-testid="ledger-subtotal">${subtotal.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">IVA %</Label>
                      <Select value={String(ivaRate)} onValueChange={(v) => upd("iva_rate", Number(v))}>
                        <SelectTrigger className="h-8 bg-white" data-testid="ledger-iva-rate"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="0">0% (exento)</SelectItem>
                          <SelectItem value="8">8% (frontera)</SelectItem>
                          <SelectItem value="16">16% (general)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase text-slate-500">Total {ivaRate > 0 ? `(con IVA)` : ""}</p>
                      <p className="font-mono font-black text-lg text-rose-700" data-testid="ledger-total-iva">${totalIva.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                  {fxRate !== 1 && (
                    <p className="text-[10px] text-slate-500 text-right italic" data-testid="ledger-mxn-preview">
                      Contable en MXN = ${(totalIva * fxRate).toLocaleString("es-MX", { minimumFractionDigits: 2 })} · fx {fxRate}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Camino simple: monto directo o qty × p.unit (usado por ingresos y gastos sin factura) */}
          {(isIng || !invoiceMode) && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-[10px] text-slate-500 uppercase">Cantidad (opc.)</Label>
                  <Input type="number" step="0.01" value={f.qty} onChange={(e) => upd("qty", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900 text-right font-mono" data-testid="ledger-qty" /></div>
                <div><Label className="text-[10px] text-slate-500 uppercase">Unidad</Label>
                  <Input value={f.unit} onChange={(e) => upd("unit", e.target.value)}
                    placeholder="ml, ton, L" className="bg-white border-slate-200 text-slate-900" data-testid="ledger-unit" /></div>
                <div><Label className="text-[10px] text-slate-500 uppercase">P.Unit. (opc.)</Label>
                  <Input type="number" step="0.01" value={f.unit_price} onChange={(e) => upd("unit_price", e.target.value)}
                    className="bg-white border-slate-200 text-slate-900 text-right font-mono" data-testid="ledger-up" /></div>
              </div>
              <div><Label className="text-[10px] text-slate-500 uppercase">Monto {parseFloat(f.qty)>0 && parseFloat(f.unit_price)>0 ? "(auto = qty × p.unit)" : "*"}</Label>
                <Input type="number" step="0.01"
                  value={parseFloat(f.qty)>0 && parseFloat(f.unit_price)>0 ? (parseFloat(f.qty)*parseFloat(f.unit_price)).toFixed(2) : f.amount}
                  onChange={(e) => upd("amount", e.target.value)}
                  disabled={parseFloat(f.qty)>0 && parseFloat(f.unit_price)>0}
                  className={`bg-white border-slate-200 text-slate-900 text-right font-mono text-lg font-bold ${isIng ? "text-emerald-700" : "text-rose-600"}`} data-testid="ledger-amount" /></div>
              {fxRate !== 1 && parseFloat(f.amount) > 0 && (
                <p className="text-[10px] text-slate-500 text-right italic">
                  Contable en MXN = ${((parseFloat(f.amount) || 0) * fxRate).toLocaleString("es-MX", { minimumFractionDigits: 2 })} · fx {fxRate}
                </p>
              )}
            </>
          )}

          <div><Label className="text-[10px] text-slate-500 uppercase">Notas / referencia</Label>
            <Textarea rows={2} value={f.notes || ""} onChange={(e) => upd("notes", e.target.value)}
              className="bg-white border-slate-200 text-slate-900" data-testid="ledger-notes" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-700 hover:bg-slate-50">Cancelar</Button>
          <Button onClick={save} disabled={busy}
            className={`${isIng ? "bg-emerald-500 hover:bg-emerald-400" : "bg-rose-500 hover:bg-rose-400"} text-slate-900 font-bold`}
            data-testid="ledger-save">
            {busy ? "Guardando…" : `Guardar ${isIng ? "ingreso" : "gasto"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Finance() {
  const { has } = usePermissions();
  const canWrite = has("finance.write");
  const canPdf = has("finance.reports");
  const [tab, setTab] = useState(() => localStorage.getItem("fin_tab") || "destajos");
  useEffect(() => { localStorage.setItem("fin_tab", tab); }, [tab]);
  const [tarifas, setTarifas] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entry, setEntry] = useState(emptyEntry());
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [budget, setBudget] = useState(null);
  const [showTarifas, setShowTarifas] = useState(false);
  const [showBudget, setShowBudget] = useState(false);
  const [showAnnualBudget, setShowAnnualBudget] = useState(false);
  const [clientEstimates, setClientEstimates] = useState([]);
  const [editingCe, setEditingCe] = useState(null);   // null = closed, "new" | {id} = open
  const [ledger, setLedger] = useState([]);
  const [ledgerCats, setLedgerCats] = useState({ ingreso: [], gasto: [] });
  const [pnl, setPnl] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [ledgerDlg, setLedgerDlg] = useState(null);    // null | {mode: "new"|"edit", entry}
  const [opsImport, setOpsImport] = useState(null);    // null | {ops_count, sources, destajo, penalizaciones}
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [estimateRange, setEstimateRange] = useState({
    date_from: new Date().toISOString().slice(0, 8) + "01",
    date_to: new Date().toISOString().slice(0, 10),
    title: "",
  });

  const month = entry.date.slice(0, 7);
  const dateFrom = `${month}-01`;
  const dateTo = `${month}-31`;

  // Iter119: filtro de tiempo DEDICADO para el Libro Contable — independiente
  // del mes de captura. Permite navegar rangos amplios sin cambiar el mes de
  // trabajo. Presets rápidos + rango custom.
  const _todayISO = new Date().toISOString().slice(0, 10);
  const _monthStart = (d) => { const x = new Date(d); x.setDate(1); return x.toISOString().slice(0, 10); };
  const _monthEnd = (d) => { const x = new Date(d); x.setMonth(x.getMonth() + 1, 0); return x.toISOString().slice(0, 10); };
  const _daysAgo = (n) => { const x = new Date(); x.setDate(x.getDate() - n); return x.toISOString().slice(0, 10); };
  const _prevMonthStart = () => { const x = new Date(); x.setMonth(x.getMonth() - 1, 1); return x.toISOString().slice(0, 10); };
  const _prevMonthEnd = () => { const x = new Date(); x.setDate(0); return x.toISOString().slice(0, 10); };
  const _yearStart = () => `${new Date().getFullYear()}-01-01`;
  const [ledgerPreset, setLedgerPreset] = useState("this_month");
  const [ledgerFrom, setLedgerFrom] = useState(_monthStart(_todayISO));
  const [ledgerTo, setLedgerTo] = useState(_todayISO);
  useEffect(() => {
    const map = {
      this_month: [_monthStart(_todayISO), _monthEnd(_todayISO)],
      prev_month: [_prevMonthStart(), _prevMonthEnd()],
      last_30: [_daysAgo(30), _todayISO],
      last_90: [_daysAgo(90), _todayISO],
      ytd: [_yearStart(), _todayISO],
      custom: [ledgerFrom, ledgerTo],
    };
    const [f, t] = map[ledgerPreset] || map.this_month;
    if (ledgerPreset !== "custom") { setLedgerFrom(f); setLedgerTo(t); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerPreset]);

  const [cfPreset, setCfPreset] = useState("this_month");
  const [cfFrom, setCfFrom] = useState(_monthStart(_todayISO));
  const [cfTo, setCfTo] = useState(_monthEnd(_todayISO));
  useEffect(() => {
    const d = new Date(); const q = Math.floor(d.getMonth() / 3) * 3;
    const map = {
      this_month: [_monthStart(_todayISO), _monthEnd(_todayISO)],
      prev_month: [_prevMonthStart(), _prevMonthEnd()],
      quarter: [`${d.getFullYear()}-${String(q + 1).padStart(2, "0")}-01`, _monthEnd(`${d.getFullYear()}-${String(q + 3).padStart(2, "0")}-01`)],
      last_90: [_daysAgo(90), _todayISO],
      ytd: [_yearStart(), _todayISO],
      year: [_yearStart(), `${d.getFullYear()}-12-31`],
    };
    if (cfPreset !== "custom") { const [f, t] = map[cfPreset]; setCfFrom(f); setCfTo(t); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfPreset]);
  const shiftCf = (dir) => {
    const a = new Date(cfFrom + "T00:00:00"), b = new Date(cfTo + "T00:00:00");
    const days = Math.round((b - a) / 86400000) + 1;
    const wholeMonth = a.getDate() === 1 && _monthEnd(cfFrom) === cfTo;
    if (wholeMonth) {
      const n = new Date(a.getFullYear(), a.getMonth() + dir, 1); const iso = n.toISOString().slice(0, 10);
      setCfFrom(iso); setCfTo(_monthEnd(iso));
    } else {
      a.setDate(a.getDate() + dir * days); b.setDate(b.getDate() + dir * days);
      setCfFrom(a.toISOString().slice(0, 10)); setCfTo(b.toISOString().slice(0, 10));
    }
    setCfPreset("custom");
  };

  const loadTarifas = useCallback(() => api.get("/finance/tarifas").then((r) => setTarifas(r.data.tarifas)).catch(() => {}), []);
  const loadEntries = useCallback(() => api.get(`/finance/entries?date_from=${dateFrom}&date_to=${dateTo}`).then((r) => setEntries(r.data || [])).catch(() => {}), [dateFrom, dateTo]);
  const loadSummary = useCallback(() => api.get(`/finance/summary?date_from=${dateFrom}&date_to=${dateTo}`).then((r) => setSummary(r.data)).catch(() => {}), [dateFrom, dateTo]);
  const loadBudget = useCallback(() => api.get(`/finance/budget?month=${month}`).then((r) => setBudget(r.data)).catch(() => {}), [month]);
  const loadClientEstimates = useCallback(() => api.get("/finance/client-estimates").then((r) => setClientEstimates(r.data || [])).catch(() => {}), []);
  const loadLedger = useCallback(() => api.get(`/finance/ledger?date_from=${ledgerFrom}&date_to=${ledgerTo}`).then((r) => setLedger(r.data || [])).catch(() => {}), [ledgerFrom, ledgerTo]);
  const loadLedgerCats = useCallback(() => api.get("/finance/ledger/categories").then((r) => setLedgerCats(r.data || {ingreso:[],gasto:[]})).catch(() => {}), []);
  const loadPnl = useCallback(() => api.get(`/finance/pnl?date_from=${dateFrom}&date_to=${dateTo}`).then((r) => setPnl(r.data)).catch(() => {}), [dateFrom, dateTo]);
  const loadCashflow = useCallback(() => api.get(`/finance/cashflow?date_from=${cfFrom}&date_to=${cfTo}`).then((r) => setCashflow(r.data)).catch(() => {}), [cfFrom, cfTo]);

  useEffect(() => { loadTarifas(); loadLedgerCats(); }, [loadTarifas, loadLedgerCats]);
  useEffect(() => { loadEntries(); loadSummary(); loadBudget(); loadClientEstimates(); loadLedger(); loadPnl(); loadCashflow(); }, [loadEntries, loadSummary, loadBudget, loadClientEstimates, loadLedger, loadPnl, loadCashflow]);

  // Real-time preview: whenever destajo or penalizaciones or tarifas change, hit /preview
  useEffect(() => {
    if (!tarifas) return;
    const timer = setTimeout(() => {
      api.post("/finance/preview", entry).then((r) => setPreview(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [entry, tarifas]);

  // Auto-import de Operaciones cuando cambia la fecha (sin botón manual).
  // Trae cantidades del día seleccionado y las penalizaciones aplicadas por
  // el auditor en cada registro de Operaciones. Es AUTOMÁTICO y ACUMULABLE:
  // cada día es un snapshot; el mes se construye sumando esos snapshots en /summary.
  useEffect(() => {
    if (!entry.date || !tarifas) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      api.get(`/finance/from-operations?date=${entry.date}`).then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        setOpsImport(d);
        // Sincronizamos las cantidades y penalizaciones que trae Operaciones.
        // El usuario puede seguir tecleando manualmente los campos _ofi/_ayd
        // si el pago se dividió con el ayudante.
        setEntry((prev) => ({
          ...prev,
          destajo: { ...(prev.destajo || {}), ...(d.destajo || {}) },
          penalizaciones: { ...(d.penalizaciones || {}) },
        }));
      }).catch(() => {});
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [entry.date, tarifas]);

  const upd = (field, v) => {
    setEntry((e) => ({ ...e, destajo: { ...e.destajo, [field]: parseFloat(v) || 0 } }));
  };

  const saveEntry = async () => {
    if (!assertProject(entry.project_id)) return;
    try {
      await api.post("/finance/entries", entry);
      toast.success("Producción del día registrada");
      setEntry(emptyEntry());
      loadEntries(); loadSummary(); loadBudget();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("¿Eliminar esta entrada?")) return;
    try { await api.delete(`/finance/entries/${id}`); loadEntries(); loadSummary(); loadBudget(); }
    catch (e) { toast.error(apiError(e)); }
  };


  const downloadEstimate = async () => {
    try {
      const p = new URLSearchParams(estimateRange).toString();
      const res = await api.get(`/finance/estimate/pdf?${p}`, { responseType: "blob" });
      downloadFile(res.data, `estimacion-${estimateRange.date_from}-${estimateRange.date_to}.pdf`);
    } catch (e) { toast.error(apiError(e)); }
  };

  // ---- Derived values from preview / summary ----
  const marginTone = useMemo(() => {
    const p = preview?.margen_pct ?? 0;
    if (p >= 25) return "emerald";
    if (p >= 18) return "amber";
    return "rose";
  }, [preview]);

  const budgetBarData = useMemo(() => {
    if (!budget) return [];
    const a = budget.actual || {};
    return [
      { name: "Ingreso", Real: a.ingreso_neto || 0, Objetivo: budget.budget?.ingreso_target || 0 },
      { name: "Costo", Real: a.costo_destajo || 0, Objetivo: budget.budget?.costo_target || 0 },
      { name: "Margen", Real: a.margen_bruto || 0, Objetivo: (budget.budget?.ingreso_target || 0) - (budget.budget?.costo_target || 0) },
    ];
  }, [budget]);

  const profitabilityData = useMemo(() => {
    if (!summary?.concepts) return [];
    return summary.concepts.map((c) => ({ ...c, name: c.label.split("·")[1]?.trim() || c.label }));
  }, [summary]);

  if (!tarifas) return <div className="text-slate-500 p-8">Cargando módulo financiero…</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 pb-2 border-b border-slate-100">
        <div className="h-11 w-11 rounded-md bg-slate-900 flex items-center justify-center shadow-sm ring-1 ring-slate-800/40">
          <CurrencyDollar size={22} weight="duotone" className="text-white" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-0.5">Destajos vs Budget · Margen bruto objetivo 18-25%</p>
          <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-none">Control Financiero Contratista</h1>
          <p className="text-[10px] text-slate-500 mt-1 italic">
            <span className="text-slate-800 font-bold">Nota contable:</span> los pagos por destajo (ml, ton, ancla, malla, viaje, hora-equipo) son <b>Costo Directo de Producción</b> — no gasto operativo.
          </p>
        </div>
        <div className="flex-1" />
        {canWrite && (
          <Button variant="outline" onClick={() => setShowTarifas(true)} data-testid="fin-open-tarifas"
            className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-amber-600 gap-1.5">
            <Gear size={14} weight="bold" /> Tarifas
          </Button>
        )}
        {canWrite && (
          <Button variant="outline" onClick={() => setShowBudget(true)} data-testid="fin-open-budget"
            className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-sky-700 gap-1.5">
            <ChartBar size={14} weight="bold" /> Budget {month}
          </Button>
        )}
        {canWrite && (
          <Button variant="outline" onClick={() => setShowAnnualBudget(true)} data-testid="fin-open-annual-budget"
            className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-amber-600 gap-1.5">
            <ChartBar size={14} weight="bold" /> Vista anual
          </Button>
        )}
        {canWrite && (
          <Button onClick={() => setPayrollOpen(true)} data-testid="fin-open-payroll"
            className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5">
            <FloppyDisk size={14} weight="bold" /> Pago a destajo
          </Button>
        )}
      </div>


      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1" data-testid="fin-tabs">
          <TabsTrigger value="libro" className="text-xs" data-testid="fin-tab-libro">📒 Libro (ingresos / egresos)</TabsTrigger>
          <TabsTrigger value="desarrollo" className="text-xs" data-testid="fin-tab-desarrollo">⛏️ Ingresos por desarrollo</TabsTrigger>
          <TabsTrigger value="destajos" className="text-xs" data-testid="fin-tab-destajos">👷 Destajos personal</TabsTrigger>
          <TabsTrigger value="especiales" className="text-xs" data-testid="fin-tab-especiales">⏰ Pagos horarios especiales</TabsTrigger>
          <TabsTrigger value="estimaciones" className="text-xs" data-testid="fin-tab-estimaciones">🧾 Estimaciones cliente</TabsTrigger>
          <TabsTrigger value="analisis" className="text-xs" data-testid="fin-tab-analisis">📈 Budget vs Real · Resumen ejecutivo</TabsTrigger>
        </TabsList>

      <TabsContent value="destajos" className="mt-4 space-y-6">
      {/* KPI Header (5 cards) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fin-header-kpis">
        <KpiCard tone="sky" label="Ingreso Proyectado (ref)" value={money(preview?.ingreso_bruto)} sub="Referencia interna · metros × tarifa cliente" testid="fin-kpi-ingreso-bruto" />
        <KpiCard tone="amber" label="Gasto Destajo (personal)" value={money(preview?.costo_destajo)} sub="Gasto operativo · pago a destajo" testid="fin-kpi-costo" />
        <KpiCard tone={marginTone} label="Margen Bruto (ref)" value={money(preview?.margen_bruto)} sub={`${preview?.margen_pct || 0}%`} testid="fin-kpi-margen" />
        <KpiCard tone={marginTone} label="% Margen (ref)" value={`${preview?.margen_pct || 0}%`} sub={preview?.margen_pct >= 18 ? "En objetivo" : "BAJO OBJETIVO"} testid="fin-kpi-margen-pct" />
        <KpiCard tone="rose" label="Riesgo / Penalizaciones" value={money(preview?.descuentos_total)} sub={`${(preview?.descuentos || []).length} activa(s) · desde Operaciones`} testid="fin-kpi-riesgo" />
      </div>

      {/* Captura diaria — auto-sincronizada con Operaciones */}
      <div className="grid grid-cols-1 gap-4">
        {/* Vista tabla — desglose de destajos en rango de fechas */}
        <DestajoRangeTable />

        {/* Descuentos aplicados (calculados a partir de cláusulas auditadas en Operaciones) */}
        {(preview?.descuentos?.length > 0) && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-1" data-testid="fin-descuentos">
            <p className="overline-label text-rose-700 flex items-center gap-1.5">
              <Warning size={12} weight="fill" /> Descuentos aplicados por cláusulas de auditor
            </p>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-0.5">
              {preview.descuentos.map((d) => (
                <div key={d.key} className="flex justify-between text-[11px]">
                  <span className="text-slate-600">{d.label}</span>
                  <span className="font-bold text-rose-700">-{money2(d.monto)}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 italic pt-1 border-t border-rose-100">
              Las cláusulas se aplican por captura directamente en <b>Operaciones</b> por el auditor asignado (admin, jefatura o supervisor).
            </p>
          </div>
        )}

        {/* -------- RESUMEN DE PAGOS Y ESTATUS · Periodo -------- */}
        <PaymentsSummaryPanel />
      </div>

      {/* Budget vs Real */}
      {budget && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="fin-budget-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-sky-700 uppercase text-xs tracking-widest">Budget vs Real · {month}</h3>
            <p className="text-[11px] text-slate-500">Objetivo margen: <b className="text-emerald-700">{budget.budget?.margen_target_pct || 20}%</b> · Actual: <b className={budget.actual?.margen_pct >= (budget.budget?.margen_target_pct || 20) ? "text-emerald-700" : "text-rose-600"}>{budget.actual?.margen_pct || 0}%</b></p>
          </div>
          {(budget.budget?.ingreso_target > 0 || budget.actual?.ingreso_neto > 0) ? (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={budgetBarData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" tickFormatter={(v) => money(v)} />
                  <Tooltip formatter={(v) => money2(v)} contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#334155" }} />
                  <Bar dataKey="Objetivo" fill="#cbd5e1" />
                  <Bar dataKey="Real" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic py-8 text-center">Configura el budget del mes con el botón «Budget» arriba para ver el comparativo.</p>
          )}
        </div>
      )}

      {/* Rentabilidad por Concepto */}
      {summary && summary.concepts?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="fin-profitability">
          <h3 className="font-heading font-bold text-emerald-700 uppercase text-xs tracking-widest mb-3">Rentabilidad por Concepto · {month}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={profitabilityData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" tickFormatter={(v) => money(v)} />
                  <YAxis dataKey="name" type="category" stroke="#64748b" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip formatter={(v) => money2(v)} contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6 }} />
                  <Bar dataKey="margen">
                    {profitabilityData.map((c, i) => (
                      <Cell key={i} fill={c.margen >= 0 ? CONCEPT_KIND_COLOR[c.kind] : "#f43f5e"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest border-b border-slate-200">
                    <th className="py-1">Concepto</th>
                    <th className="py-1 text-right">Cant.</th>
                    <th className="py-1 text-right">Ingreso</th>
                    <th className="py-1 text-right">Costo</th>
                    <th className="py-1 text-right">Margen</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.concepts.map((c) => (
                    <tr key={c.key} data-testid={`fin-prof-row-${c.key}`} className="border-b border-slate-100">
                      <td className="py-1 text-slate-800">{c.label}</td>
                      <td className="py-1 text-right text-slate-500">{c.qty} {c.unit}</td>
                      <td className="py-1 text-right text-sky-700">{money(c.ingreso_bruto)}</td>
                      <td className="py-1 text-right text-amber-600">{money(c.costo)}</td>
                      <td className={`py-1 text-right font-bold ${c.margen >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                        {money(c.margen)} ({c.margen_pct}%)
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recent entries */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="fin-entries-table">
        <h3 className="font-heading font-bold text-slate-700 uppercase text-xs tracking-widest mb-3">Entradas del mes ({entries.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest border-b border-slate-200">
                <th className="py-1">Fecha</th>
                <th className="py-1">Zona</th>
                <th className="py-1 text-right">Costo destajo</th>
                <th className="py-1 text-right">Ingreso neto</th>
                <th className="py-1 text-right">Margen</th>
                <th className="py-1">Descuentos</th>
                <th className="py-1 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500 italic">Sin entradas este mes.</td></tr>}
              {entries.map((e) => {
                const c = e.calculated || {};
                const dOK = c.margen_pct >= 18;
                return (
                  <tr key={e.id} data-testid={`fin-entry-row-${e.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-1 text-slate-800">{e.date}</td>
                    <td className="py-1 text-slate-500">{e.area || "-"}</td>
                    <td className="py-1 text-right text-amber-600">{money(c.costo_destajo)}</td>
                    <td className="py-1 text-right text-sky-700">{money(c.ingreso_neto)}</td>
                    <td className={`py-1 text-right font-bold ${dOK ? "text-emerald-700" : "text-rose-600"}`}>{money(c.margen_bruto)} ({c.margen_pct}%)</td>
                    <td className="py-1 text-slate-500 text-[10px]">{(c.descuentos || []).length} penal.</td>
                    <td className="py-1 text-right">
                      {canWrite && <button onClick={() => removeEntry(e.id)} className="text-slate-500 hover:text-rose-600 text-xs">Eliminar</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


      </TabsContent>

      <TabsContent value="libro" className="mt-4 space-y-6">
      <ProfitabilityPanel canWrite={canWrite} />
      {/* ========= P&L / Estado de Resultados consolidado ========= */}
      {pnl && (
        <div className="bg-white border border-emerald-200 rounded-lg p-4" data-testid="fin-pnl">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} weight="fill" className="text-emerald-700" />
            <h3 className="font-heading font-bold text-emerald-700 uppercase text-xs tracking-widest">Estado de Resultados · {month}</h3>
            <span className="text-[10px] text-slate-500">— destajo + libro contable</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
              <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">INGRESO TOTAL</p>
              <p className="text-lg font-heading font-black text-emerald-700" data-testid="pnl-ingreso-total">{money(pnl.ingreso_total)}</p>
              <p className="text-[10px] text-slate-500">Desarrollo (auto) {money(pnl.ingreso_desarrollo)} · otros manuales {money((pnl.ingreso_manual || 0) - (pnl.ingreso_desarrollo || 0))}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2">
              <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">(–) COSTO PROD.</p>
              <p className="text-lg font-heading font-black text-amber-700" data-testid="pnl-costo-prod">-{money(pnl.costo_produccion)}</p>
              <p className="text-[10px] text-slate-500">Destajo a personal</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded p-2">
              <p className="text-[10px] uppercase tracking-widest text-orange-700 font-bold">(–) GASTOS OP.</p>
              <p className="text-lg font-heading font-black text-orange-700" data-testid="pnl-gasto-op">-{money(pnl.gasto_operativo)}</p>
              <p className="text-[10px] text-slate-500">Pagos especiales (auto) {money(pnl.gasto_pagos_especiales)} · combustible, admin, etc.</p>
            </div>
            <div className="bg-slate-100 border border-slate-200 rounded p-2">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">= UTILIDAD NETA</p>
              <p className={`text-lg font-heading font-black ${pnl.utilidad_neta >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="pnl-utilidad">
                {money(pnl.utilidad_neta)}
              </p>
              <p className="text-[10px] text-slate-500">Bruta {money(pnl.utilidad_bruta)}</p>
            </div>
            <div className={`border rounded p-2 ${pnl.margen_neto_pct >= 18 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <p className="text-[10px] uppercase tracking-widest text-slate-700 font-bold">% MARGEN NETO</p>
              <p className={`text-2xl font-heading font-black ${pnl.margen_neto_pct >= 18 ? "text-emerald-700" : "text-rose-600"}`} data-testid="pnl-margen-pct">
                {pnl.margen_neto_pct}%
              </p>
              <p className="text-[10px] text-slate-500">Objetivo ≥ 18%</p>
            </div>
          </div>
        </div>
      )}

      {/* ========= Cash flow chart (line + area accumulated) ========= */}
      {cashflow && (
        <div className="bg-white border border-cyan-200 rounded-lg p-4" data-testid="fin-cashflow">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <TrendUp size={16} weight="fill" className="text-cyan-700" />
            <h3 className="font-heading font-bold text-cyan-700 uppercase text-xs tracking-widest">Flujo de Caja</h3>
            <span className="text-[10px] text-slate-500">— acumulado día a día · {cfFrom} → {cfTo}</span>
            <div className="flex-1" />
            <div className="flex items-center gap-1">
              <button onClick={() => shiftCf(-1)} className="h-7 px-2 rounded border text-xs hover:bg-slate-100" title="Periodo anterior" data-testid="fin-cf-prev">◀</button>
              <select value={cfPreset} onChange={(e) => setCfPreset(e.target.value)} className="h-7 text-xs border rounded px-1 bg-white" data-testid="fin-cf-preset">
                <option value="this_month">Este mes</option><option value="prev_month">Mes anterior</option><option value="quarter">Trimestre</option>
                <option value="last_90">Últimos 90 días</option><option value="ytd">Año a la fecha</option><option value="year">Año completo</option><option value="custom">Personalizado</option>
              </select>
              <button onClick={() => shiftCf(1)} className="h-7 px-2 rounded border text-xs hover:bg-slate-100" title="Periodo siguiente" data-testid="fin-cf-next">▶</button>
              <input type="date" value={cfFrom} onChange={(e) => { setCfFrom(e.target.value); setCfPreset("custom"); }} className="h-7 text-xs border rounded px-1" data-testid="fin-cf-from" />
              <input type="date" value={cfTo} onChange={(e) => { setCfTo(e.target.value); setCfPreset("custom"); }} className="h-7 text-xs border rounded px-1" data-testid="fin-cf-to" />
            </div>
          </div>
          <div className="text-xs flex flex-wrap gap-4 mb-2">
              <span className="text-emerald-700">Total ingresos: <b>{money(cashflow.ingreso_total)}</b></span>
              <span className="text-rose-600">Total gastos: <b>{money(cashflow.gasto_total)}</b></span>
              <span className={cashflow.balance_final >= 0 ? "text-cyan-700" : "text-rose-600"}>
                Balance: <b>{money(cashflow.balance_final)}</b>
              </span>
          </div>
          {(!cashflow.series || cashflow.series.length === 0) && <p className="text-xs text-slate-400 py-6 text-center">Sin movimientos en el periodo.</p>}
          {cashflow.series?.length > 0 && (
          <div style={{ width: "100%", height: 260 }} data-testid="fin-cashflow-chart">
            <ResponsiveContainer>
              <ComposedChart data={cashflow.series}>
                <defs>
                  <linearGradient id="balPos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis stroke="#64748b" tick={{ fontSize: 10 }} tickFormatter={(v) => money(v)} />
                <Tooltip formatter={(v) => money2(v)}
                  contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6, color: "#0f172a" }}
                  labelStyle={{ color: "#64748b" }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#334155" }} />
                <ReferenceLine y={0} stroke="#f43f5e" strokeDasharray="4 4" strokeOpacity={0.5} />
                <Area type="monotone" dataKey="balance_acumulado" name="Balance" stroke="#22d3ee" fill="url(#balPos)" strokeWidth={2} />
                <Line type="monotone" dataKey="ingreso_acumulado" name="Ingreso acumulado" stroke="#10b981" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="gasto_acumulado" name="Gasto acumulado" stroke="#f43f5e" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          )}
          {cashflow.crossings?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {cashflow.crossings.map((c, i) => (
                <span key={i} data-testid={`fin-cashflow-cross-${i}`}
                      className={`text-[10px] px-2 py-0.5 rounded font-bold ${c.type === "negative" ? "bg-rose-900/60 text-rose-200" : "bg-emerald-900/60 text-emerald-200"}`}>
                  {c.type === "negative" ? "⚠ Balance negativo desde" : "✓ Recuperación desde"} {c.date}
                </span>
              ))}
            </div>
          )}
        </div>
      )}


      {/* ========= Libro Contable (INGRESO / GASTO manuales) ========= */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="fin-ledger">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <BookOpen size={16} weight="fill" className="text-slate-700" />
          <h3 className="font-heading font-bold text-slate-800 uppercase text-xs tracking-widest">Libro Contable · Ingresos y Gastos</h3>
          <span className="text-[10px] text-slate-500">— manuales + automáticos (destajos, desarrollo, pagos especiales)</span>
          <div className="flex-1" />
          {canWrite && (
            <>
              <Button onClick={() => setLedgerDlg({ mode: "new", entry: { date: entry.date, project_id: entry.project_id || defaultProject(), kind: "ingreso", category: "acumulado_ml", concept: "", amount: 0, qty: 0, unit: "ml", unit_price: 0, notes: "" } })}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 gap-1.5" data-testid="fin-ledger-new-income">
                <PlusCircle size={14} weight="bold" /> + Ingreso
              </Button>
              <Button onClick={() => setLedgerDlg({ mode: "new", entry: { date: entry.date, project_id: entry.project_id || defaultProject(), kind: "gasto", category: "combustible", concept: "", amount: 0, qty: 0, unit: "", unit_price: 0, notes: "" } })}
                className="bg-rose-600 hover:bg-rose-500 text-white h-8 gap-1.5" data-testid="fin-ledger-new-expense">
                <MinusCircle size={14} weight="bold" /> – Gasto
              </Button>
            </>
          )}
        </div>
        {/* Iter119: filtro de tiempo del libro contable */}
        <div className="flex flex-wrap items-end gap-2 mb-3 p-2 bg-slate-50 border border-slate-200 rounded">
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Periodo</Label>
            <Select value={ledgerPreset} onValueChange={setLedgerPreset}>
              <SelectTrigger className="h-8 w-44 bg-white" data-testid="fin-ledger-preset"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="this_month">Este mes</SelectItem>
                <SelectItem value="prev_month">Mes anterior</SelectItem>
                <SelectItem value="last_30">Últimos 30 días</SelectItem>
                <SelectItem value="last_90">Últimos 90 días</SelectItem>
                <SelectItem value="ytd">Año en curso (YTD)</SelectItem>
                <SelectItem value="custom">Rango personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Desde</Label>
            <Input type="date" value={ledgerFrom}
              onChange={(e) => { setLedgerFrom(e.target.value); setLedgerPreset("custom"); }}
              className="h-8 w-40 bg-white" data-testid="fin-ledger-from" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Hasta</Label>
            <Input type="date" value={ledgerTo}
              onChange={(e) => { setLedgerTo(e.target.value); setLedgerPreset("custom"); }}
              className="h-8 w-40 bg-white" data-testid="fin-ledger-to" />
          </div>
          <div className="flex-1 text-right text-[11px] text-slate-500" data-testid="fin-ledger-range-info">
            {ledger.length} movimientos · <b>{ledgerFrom}</b> → <b>{ledgerTo}</b>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 italic mb-2">Aquí capturas movimientos que <b>no vienen del destajo diario</b>. Ejemplos: cobro acumulado de ml/ton al cliente, combustible, refacciones, viáticos admin, bonos.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest border-b border-slate-200">
                <th className="py-1">Fecha</th>
                <th className="py-1">Tipo</th>
                <th className="py-1">Categoría</th>
                <th className="py-1">Concepto</th>
                <th className="py-1 text-right">Cantidad</th>
                <th className="py-1 text-right">P.Unit.</th>
                <th className="py-1 text-right">Monto</th>
                <th className="py-1 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {ledger.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500 italic">Sin movimientos manuales. Añade ingresos o gastos con los botones arriba.</td></tr>}
              {ledger.map((l) => (
                <tr key={l.id} data-testid={`fin-ledger-row-${l.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${l.auto ? "bg-sky-50/40" : ""}`}>
                  <td className="py-1 text-slate-500">{l.date}</td>
                  <td className="py-1 whitespace-nowrap">
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${l.kind === "ingreso" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {l.kind === "ingreso" ? "INGRESO" : "GASTO"}
                    </span>
                    {l.auto && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-sky-100 text-sky-800" title="Generado automáticamente desde Ingresos por desarrollo / Pagos especiales" data-testid={`fin-ledger-auto-${l.id}`}>AUTO</span>}
                  </td>
                  <td className="py-1 text-slate-500 text-[10px]">{l.category}</td>
                  <td className="py-1 text-slate-800">{l.concept}</td>
                  <td className="py-1 text-right text-slate-500">{l.qty > 0 ? `${l.qty} ${l.unit}` : "-"}</td>
                  <td className="py-1 text-right text-slate-500">{l.unit_price > 0 ? money(l.unit_price) : "-"}</td>
                  <td className={`py-1 text-right font-bold ${l.kind === "ingreso" ? "text-emerald-700" : "text-rose-600"}`}>
                    {l.kind === "ingreso" ? "+" : "-"}{money(l.amount)}
                  </td>
                  <td className="py-1 text-right">
                    {(l.items || []).length > 0 && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/finance/ledger/${l.id}/invoice-pdf`}
                         target="_blank" rel="noopener noreferrer"
                         className="text-slate-500 hover:text-blue-700 text-[11px] mr-2"
                         data-testid={`fin-ledger-pdf-${l.id}`}
                         title="PDF de factura (partidas + IVA)">📄 PDF</a>
                    )}
                    {canWrite && !l.auto && (
                      <>
                        <button onClick={() => setLedgerDlg({ mode: "edit", entry: l })} className="text-slate-500 hover:text-amber-600 text-[11px] mr-2" data-testid={`fin-ledger-edit-${l.id}`}>Editar</button>
                        <button onClick={async () => {
                          if (!window.confirm("¿Eliminar este movimiento?")) return;
                          try { await api.delete(`/finance/ledger/${l.id}`); loadLedger(); loadPnl(); }
                          catch (e) { toast.error(apiError(e)); }
                        }} className="text-slate-500 hover:text-rose-600 text-[11px]" data-testid={`fin-ledger-del-${l.id}`}>Eliminar</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      </TabsContent>

      <TabsContent value="estimaciones" className="mt-4 space-y-6">
      {/* ========= Estimaciones al Cliente (formal, con firmas y anexos) ========= */}
      <div className="bg-white border border-sky-200 rounded-lg p-4" data-testid="fin-client-estimates">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Signature size={18} weight="fill" className="text-sky-700" />
            <h3 className="font-heading font-bold text-sky-700 uppercase text-xs tracking-widest">Estimaciones al Cliente · Cobro formal</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500">{clientEstimates.length} emitida(s)</span>
            {canWrite && (
              <Button onClick={() => setEditingCe("new")} className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-bold h-8 gap-1.5" data-testid="fin-ce-new">
                <Plus size={13} weight="bold" /> Nueva estimación cliente
              </Button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-slate-500 -mt-1 mb-3">Documento formal para cobro con firmas (elaborado / revisado / aprobado cliente) y anexos (fotos justificativas y planos).</p>
        {clientEstimates.length === 0 ? (
          <p className="text-xs text-slate-500 italic text-center py-6 bg-slate-50 border border-slate-200 rounded">Sin estimaciones al cliente todavía.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest border-b border-slate-200">
                  <th className="py-1">Folio</th>
                  <th className="py-1">Cliente</th>
                  <th className="py-1">Período</th>
                  <th className="py-1">Emisión</th>
                  <th className="py-1 text-right">Total</th>
                  <th className="py-1">Estado</th>
                  <th className="py-1 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {clientEstimates.map((ce) => {
                  const total = ce.totals?.total ?? 0;
                  const stColor = { draft: "bg-slate-100 text-slate-700", sent: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", paid: "bg-sky-100 text-sky-800" }[ce.status] || "bg-slate-100 text-slate-700";
                  return (
                    <tr key={ce.id} data-testid={`fin-ce-row-${ce.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-1 font-mono text-amber-700 font-bold">{ce.folio}</td>
                      <td className="py-1 text-slate-800">{ce.client_name || "-"}</td>
                      <td className="py-1 text-slate-500">{ce.date_from} → {ce.date_to}</td>
                      <td className="py-1 text-slate-500">{ce.issue_date}</td>
                      <td className="py-1 text-right font-bold text-emerald-700">${Number(total).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-1"><span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded ${stColor}`}>{ce.status}</span></td>
                      <td className="py-1 text-right">
                        <button onClick={() => setEditingCe({ id: ce.id })} className="text-sky-700 hover:text-sky-700 text-[11px] mr-3" data-testid={`fin-ce-edit-${ce.id}`}>Abrir</button>
                        {canWrite && (
                          <button onClick={async () => {
                            if (!window.confirm(`¿Eliminar la estimación ${ce.folio}?`)) return;
                            try { await api.delete(`/finance/client-estimates/${ce.id}`); toast.success("Estimación eliminada"); loadClientEstimates(); }
                            catch (e) { toast.error(apiError(e)); }
                          }} className="text-slate-500 hover:text-rose-600 text-[11px]" data-testid={`fin-ce-del-${ce.id}`}>Eliminar</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Estimate generator */}
      {canPdf && (
        <div className="bg-white border border-amber-200 rounded-lg p-4" data-testid="fin-estimate">
          <h3 className="font-heading font-bold text-amber-600 uppercase text-xs tracking-widest mb-3 flex items-center gap-2">
            <FilePdf size={14} weight="fill" /> Reporte de producción del período
          </h3>
          <p className="text-[11px] text-slate-500 -mt-2 mb-2">Resumen agregado sin firmas ni anexos (para uso interno).</p>
          <div className="grid md:grid-cols-4 gap-3">
            <div><Label className="text-[10px] text-slate-500 uppercase">Desde</Label>
              <Input type="date" value={estimateRange.date_from} onChange={(e) => setEstimateRange({ ...estimateRange, date_from: e.target.value })}
                className="bg-white border-slate-200 text-slate-900" data-testid="fin-estimate-from" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Hasta</Label>
              <Input type="date" value={estimateRange.date_to} onChange={(e) => setEstimateRange({ ...estimateRange, date_to: e.target.value })}
                className="bg-white border-slate-200 text-slate-900" data-testid="fin-estimate-to" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Título (opcional)</Label>
              <Input value={estimateRange.title} onChange={(e) => setEstimateRange({ ...estimateRange, title: e.target.value })}
                placeholder="Estimación semanal N°X" className="bg-white border-slate-200 text-slate-900" data-testid="fin-estimate-title" /></div>
            <div className="flex items-end">
              <Button onClick={downloadEstimate} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold gap-1.5 w-full" data-testid="fin-estimate-generate">
                <FilePdf size={14} weight="bold" /> Generar PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      </TabsContent>

      <TabsContent value="desarrollo" className="mt-4"><DevIncomePanel canWrite={canWrite} /></TabsContent>
      <TabsContent value="especiales" className="mt-4"><SpecialPaymentsPanel canWrite={canWrite} /></TabsContent>
      <TabsContent value="analisis" className="mt-4"><FinancialAnalysis embedded /></TabsContent>
      </Tabs>

      {showTarifas && <TarifasDialog tarifas={tarifas} onClose={() => setShowTarifas(false)}
        onSaved={() => { setShowTarifas(false); loadTarifas(); loadEntries(); loadSummary(); }} />}
      <ContractorPayrollDialog open={payrollOpen} onOpenChange={setPayrollOpen} canReports={canPdf} />
      {showBudget && <BudgetDialog month={month} current={budget?.budget}
        onClose={() => setShowBudget(false)} onSaved={() => { setShowBudget(false); loadBudget(); }} />}
      {showAnnualBudget && (
        <AnnualBudgetDialog
          initialYear={parseInt(month.slice(0, 4), 10)}
          onClose={() => setShowAnnualBudget(false)}
          onSaved={() => { loadBudget(); loadSummary(); }}
        />
      )}
      {editingCe && (
        <ClientEstimateDialog
          estimateId={editingCe === "new" ? null : editingCe.id}
          onClose={() => setEditingCe(null)}
          onSaved={() => { loadClientEstimates(); }}
        />
      )}
      {ledgerDlg && (
        <LedgerDialog
          mode={ledgerDlg.mode}
          entry={ledgerDlg.entry}
          cats={ledgerCats}
          onClose={() => setLedgerDlg(null)}
          onSaved={() => { setLedgerDlg(null); loadLedger(); loadPnl(); }}
        />
      )}
    </div>
  );
}


// ============================================================================
// PaymentsSummaryPanel · Resumen de pagos + estatus por trabajador (rango).
// Se muestra debajo de la Captura Diaria. Sirve para saber en un vistazo
// qué operadores tienen su pago BLOQUEADO por cláusulas del auditor.
// ============================================================================
function PaymentsSummaryPanel() {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
  const iso = (d) => d.toISOString().slice(0, 10);
  const [range, setRange] = useState({
    date_from: iso(new Date(monday.getTime() - 21 * 86400e3)),
    date_to: iso(today),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!range.date_from || !range.date_to) return;
    setLoading(true);
    api.get(`/finance/payments-summary?date_from=${range.date_from}&date_to=${range.date_to}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const statusStyle = {
    bloqueado: { cls: "bg-red-100 text-red-700 border-red-300", label: "⚠ Pago no procede", tone: "text-red-700" },
    reducido:  { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Reducido por cláusulas", tone: "text-amber-700" },
    procede:   { cls: "bg-emerald-100 text-emerald-700 border-emerald-300", label: "Procede", tone: "text-emerald-700" },
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="fin-payments-summary">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading font-bold text-slate-800 uppercase text-xs tracking-widest flex items-center gap-1.5">
          <ChartBar size={14} weight="bold" className="text-emerald-700" />
          Resumen de pagos y estatus · Periodo
        </h3>
        <div className="ml-auto flex gap-2 items-center">
          <Label className="text-[10px] uppercase text-slate-500 font-bold">Del</Label>
          <Input type="date" value={range.date_from}
            onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))}
            className="h-8 w-36" data-testid="fin-payments-from" />
          <Label className="text-[10px] uppercase text-slate-500 font-bold">Al</Label>
          <Input type="date" value={range.date_to}
            onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))}
            className="h-8 w-36" data-testid="fin-payments-to" />
        </div>
      </div>

      {loading && <p className="text-xs text-slate-500 italic">Cargando resumen…</p>}

      {data && !loading && Array.isArray(data.workers) && data.totals && data.counts && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div className="bg-slate-50 border border-slate-200 rounded p-2">
              <p className="overline-label text-slate-600">Trabajadores</p>
              <p className="font-heading font-black text-lg text-slate-900">{data.totals.workers}</p>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded p-2">
              <p className="overline-label text-sky-700">Bruto acumulado</p>
              <p className="font-heading font-black text-lg text-sky-800">{money2(data.totals.gross)}</p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded p-2">
              <p className="overline-label text-rose-700">Descuentos auditor</p>
              <p className="font-heading font-black text-lg text-rose-700">-{money2(data.totals.deductions)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
              <p className="overline-label text-emerald-700">Neto a pagar</p>
              <p className="font-heading font-black text-lg text-emerald-800">{money2(data.totals.net)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded p-2 flex flex-col justify-center">
              <p className="overline-label text-slate-500">Estatus</p>
              <p className="text-[11px] font-bold">
                <span className="text-red-700">{data.counts.bloqueado}</span> bloqueados ·{" "}
                <span className="text-amber-700">{data.counts.reducido}</span> reducidos ·{" "}
                <span className="text-emerald-700">{data.counts.procede}</span> ok
              </p>
            </div>
          </div>

          {/* Tabla por trabajador */}
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="p-2 text-left">Trabajador</th>
                  <th className="p-2 text-left">Puesto</th>
                  <th className="p-2 text-right">Días</th>
                  <th className="p-2 text-right">Bruto</th>
                  <th className="p-2 text-right">Descuentos</th>
                  <th className="p-2 text-right">Neto</th>
                  <th className="p-2 text-left">Cláusulas aplicadas</th>
                  <th className="p-2 text-center">Estatus</th>
                  <th className="p-2 text-center">Firmas</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.workers.map((w) => {
                  const st = statusStyle[w.estatus] || statusStyle.procede;
                  return (
                    <tr key={w.worker_id || w.name} data-testid={`fin-pay-row-${w.worker_id || w.name}`}
                        className={w.estatus === "bloqueado" ? "bg-red-50" : "hover:bg-slate-50"}>
                      <td className="p-2 font-semibold text-slate-900">{w.name}</td>
                      <td className="p-2 text-slate-500">{w.position || "—"}</td>
                      <td className="p-2 text-right font-mono">{w.days_worked}</td>
                      <td className="p-2 text-right font-mono">{money2(w.gross)}</td>
                      <td className="p-2 text-right font-mono text-rose-700">
                        {w.deductions_total > 0 ? `-${money2(w.deductions_total)}` : "—"}
                      </td>
                      <td className={`p-2 text-right font-mono font-black ${st.tone} ${w.estatus === "bloqueado" ? "line-through" : ""}`}>
                        {money2(w.net)}
                      </td>
                      <td className="p-2">
                        {w.clauses.length === 0 ? (
                          <span className="text-slate-400 italic">Sin cláusulas</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {w.clauses.map((c) => (
                              <span key={c.key} className="text-[10px] bg-rose-100 text-rose-800 border border-rose-200 px-1.5 py-0.5 rounded"
                                    title={`${c.count}x · -${money2(c.amount)}`}>
                                {c.label.replace(" (auditor)", "")} ({c.count}x)
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        <span className={`inline-block text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${st.cls}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="p-2 text-center text-[11px] font-mono">
                        <span className="text-emerald-700">{w.weeks_signed}</span>/{w.weeks}
                        {w.weeks_pending > 0 && (
                          <span className="text-amber-700 ml-1">({w.weeks_pending} pend)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {data.workers.length === 0 && (
                  <tr><td colSpan={9} className="p-6 text-center text-slate-400 italic">
                    Sin registros de nómina en el rango seleccionado.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data.counts.bloqueado > 0 && (
            <p className="text-[10px] italic text-red-700 border-t border-red-100 pt-1">
              ⚠ Los trabajadores marcados como <b>Pago no procede</b> ya reciben una notificación
              visible en su vista &ldquo;Mis Recibos Semanales&rdquo; indicando que las cláusulas del auditor
              anularon el pago del periodo.
            </p>
          )}
        </>
      )}
    </div>
  );
}
